import { useMutation, useQuery } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useEffect, useState } from "react"

import { AxiosError } from "axios"
import {
  type Body_authentication_login_access_token as AccessToken,
  type ApiError,
  AuthenticationService,
  type UserPrivate,
  UsersService,
} from "../client"
import { useAuthContext } from "../components/UI/auth-provider"

const useAuth = () => {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()

  const { token, login: setToken, logout: contextLogout } = useAuthContext()

  const { data: user, isLoading } = useQuery<UserPrivate | null, Error>({
    queryKey: ["currentUser"],
    queryFn: UsersService.readUserMe,
    enabled: !!token,
    retry: 1,
  })

  const loginRequest = async (data: AccessToken) => {
    return AuthenticationService.loginAccessToken({
      formData: data,
    })
  }

  const loginMutation = useMutation({
    mutationFn: loginRequest,
    onSuccess: (response) => {
      setToken(response.access_token)

      localStorage.setItem(
        "user_permissions",
        JSON.stringify(response.user_permissions ?? "")
      )

      navigate({ to: "/" })
    },
    onError: (err: ApiError) => {
      let errDetail = (err.body as any)?.detail

      if (err instanceof AxiosError) {
        errDetail = err.message
      }

      if (Array.isArray(errDetail)) {
        errDetail = "Etwas ist schief gelaufen"
      }

      setError(errDetail)
    },
  })

  const logout = () => {
    contextLogout()
    localStorage.removeItem("user_permissions")
    navigate({ to: "/login" })
  }

  useEffect(() => {
    if (!user && !isLoading && token) {
      logout()
    }
  }, [user, isLoading, token])

  return {
    loginMutation,
    logout,
    user,
    isLoading,
    error,
    resetError: () => setError(null),
  }
}

export default useAuth
